import React, { useEffect, useMemo, useState } from 'react';
import { ToggleButton, ToggleButtonGroup } from '@zillow/constellation-10';
import {
  clearAndAppendMessageInputTextStore,
  isAddMessageInputFocusedStore,
} from '../../../../lib/inboxAddMessageStores';
import type { ConversationMessagesData } from '../../../../utils/conversationMessageUtils';
import type { TriggerLocation } from '../../../../constants/clickstreamEvents/globals';
import {
  emitQuickResponseAcceptVouchersNoEvent,
  emitQuickResponseAcceptVouchersYesEvent,
  emitQuickResponseAvailableEvent,
  emitQuickResponseNotAvailableEvent,
  QUICK_RESPONSE_ACCEPT_VOUCHERS_NO_EVENT_TYPE_ID,
  QUICK_RESPONSE_ACCEPT_VOUCHERS_YES_EVENT_TYPE_ID,
  QUICK_RESPONSE_AVAILABLE_EVENT_TYPE_ID,
  QUICK_RESPONSE_NOT_AVAILABLE_EVENT_TYPE_ID,
} from '../../../../constants/clickstreamEvents/quickResponse';
import useQuickResponseHelper from '../../../../hooks/useQuickResponseHelper';
import { useAuth } from '../../../../AuthProvider';

type quickResponseResults = ConversationMessagesData['quickResponses'] | null;
type quickResponseTextType = string | null | undefined;

type Props = {
  quickResponses: quickResponseResults;
  isQuickResponseLoadedFirstTime: boolean;
  isMessageInputEmpty: boolean;
  onQuickResponseSelect: (selectedQuickResponseValue: string | null | undefined) => void;
  listingAlias: string;
  leadId?: string | null;
  triggerLocation: TriggerLocation;
};

type QuickResponseID = 'availability-0' | 'availability-1' | 'section8-0' | 'section8-1';

const QUICK_RESPONSE_EVENT_MAP = {
  'availability-0': emitQuickResponseAvailableEvent,
  'availability-1': emitQuickResponseNotAvailableEvent,
  'section8-0': emitQuickResponseAcceptVouchersYesEvent,
  'section8-1': emitQuickResponseAcceptVouchersNoEvent,
} as const;

const QUICK_RESPONSE_ID_MAP = {
  'availability-0': QUICK_RESPONSE_AVAILABLE_EVENT_TYPE_ID,
  'availability-1': QUICK_RESPONSE_NOT_AVAILABLE_EVENT_TYPE_ID,
  'section8-0': QUICK_RESPONSE_ACCEPT_VOUCHERS_YES_EVENT_TYPE_ID,
  'section8-1': QUICK_RESPONSE_ACCEPT_VOUCHERS_NO_EVENT_TYPE_ID,
} as const;

const QuickResponse = ({
  quickResponses,
  isQuickResponseLoadedFirstTime,
  isMessageInputEmpty,
  onQuickResponseSelect,
  listingAlias,
  leadId,
  triggerLocation,
}: Props) => {
  const { prediction, suggestions } = quickResponses || {};
  const { user } = useAuth();
  const { getQuickResponseQueryParams, removeQuickResponseQueryParams } = useQuickResponseHelper();
  const { utmContentValue, predictionValue } = getQuickResponseQueryParams();

  // cache the result of getSelectedCTAValueFromEmail between re-renders
  const getSelectedCTAValueFromEmail = useMemo(() => {
    const emailSelectedSuggestion = suggestions?.find((suggestion) => suggestion?.id === utmContentValue);
    const emailSelectedText = emailSelectedSuggestion ? emailSelectedSuggestion.text : '';
    const shouldShowDefaultCTA = predictionValue === prediction && emailSelectedText !== '';
    return shouldShowDefaultCTA ? emailSelectedText : undefined;
  }, [prediction, suggestions, utmContentValue, predictionValue]);

  const [toggleButtonState, setToggleButtonState] = useState(getSelectedCTAValueFromEmail || '');

  const triggerClickstreamEventForId = (id: QuickResponseID) => {
    const clickstreamEvent = QUICK_RESPONSE_EVENT_MAP[id];
    clickstreamEvent?.({ user, listing: { listingAlias }, triggerLocation, lead: { leadId } });
  };

  useEffect(() => {
    if (getSelectedCTAValueFromEmail && isQuickResponseLoadedFirstTime) {
      clearAndAppendMessageInputTextStore.set(getSelectedCTAValueFromEmail);

      if (utmContentValue) {
        triggerClickstreamEventForId(utmContentValue as QuickResponseID);
      }
      // clear the utm_content query parameter from the url to prevent firing the clickstream events from the email response again
      removeQuickResponseQueryParams();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [getSelectedCTAValueFromEmail, isQuickResponseLoadedFirstTime, utmContentValue]);

  const handleQuickResponseClick = (
    quickResponseText: quickResponseTextType,
    selectedQuickResponseId: string | null | undefined,
  ) => {
    clearAndAppendMessageInputTextStore.set(quickResponseText || '');
    isAddMessageInputFocusedStore.set(true);

    if (selectedQuickResponseId) {
      triggerClickstreamEventForId(selectedQuickResponseId as QuickResponseID);
    }
    onQuickResponseSelect(selectedQuickResponseId);
  };

  return (
    <ToggleButtonGroup
      conjoined={false}
      value={!isMessageInputEmpty ? toggleButtonState : ''}
      onValueChange={(value) => {
        if (typeof value === 'string') {
          setToggleButtonState(value);
        }
      }}
      aria-label="Quick Response Selections"
      role="group"
      size="sm"
      css={{
        display: 'flex',
        flexWrap: 'wrap',
        marginInline: 'default',
        marginBottom: 'default',
      }}
    >
      {suggestions?.map((suggestion) => {
        const { id, text } = suggestion || {};

        if (!text) return null;

        return (
          <ToggleButton
            data-eventid={QUICK_RESPONSE_ID_MAP[id as QuickResponseID]}
            key={id}
            value={text}
            onClick={() => handleQuickResponseClick(text, id)}
            css={{
              borderRadius: 'pill',
              maxWidth: 'fit-content',
              whiteSpace: 'noWrap',
            }}
          >
            {text}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
};

export default React.memo(QuickResponse);
